import React, { useState, useEffect } from "react";
import { generateLicenseKey } from "utils/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetCurrentEmployeeInfo } from "hooks/useGetCurrentEmployee";
import { useToast } from "hooks/useToast";
import License from "db/license";
import { Dropdown } from "primereact/dropdown";
import { useGetFrequentlyUsedValues } from "hooks/useGetFrequentlyUsedValues";
import DropdownField from "db/dropdownField";

export const SettingTabLicenseForm = () => {
  const [params] = useSearchParams();
  const formType = params.get('type');
  const id = params.get('id');
  const initialLicenseVal = {
    key: formType === "add" ? generateLicenseKey() : "",
    customer: "",
    memo: "",
    is_active: false
  };
  const navigate = useNavigate();
  const [license, setLicense] = useState(initialLicenseVal);
  const { is_admin } = useGetCurrentEmployeeInfo();
  const { showSuccess, showError } = useToast();
  const { Customers } = useGetFrequentlyUsedValues({});
  const [licenseType, setLicenseType] = useState([]);

  const fetchLicenseById = async (licenseID) => {
    const { data } = await License.getLicenseByID(licenseID);

    if (data) {
      setLicense(data[0]);
    }
  };

  const fetchLicenseType = async () => {
    const { data } = await DropdownField.getDataByType("License Type");

    if (data) {
      setLicenseType(data);
    }
  };

  useEffect(() => {
    fetchLicenseType();

    if (formType === "edit") {
      fetchLicenseById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (["is_active"].indexOf(name) !== -1) {
      setLicense({
        ...license,
        [name]: checked,
      });
    } else {
      setLicense({
        ...license,
        [name]: value,
      });
    }
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();

    if (!license.customer) {
      showError("Wanring", "Customer field is required.");
      return;
    }

    const data = await License.upsertLicense(license);

    if (data.data) {
      if (formType === "add") {
        showSuccess("Creation successful!", "New license is created.");
      } else {
        showSuccess("Edit successful!", "Your changes are saved successfully.");
      }
    }

    if (data.error) {
      console.log(data.error);
      showError();
    }

    navigate(-1);
  };

  return (
    <div className="relative h-full max-h-full w-full align-middle">
      <div className="border-b bg-white shadow p-4">
        <h1 className="subtitle md:title">License</h1>
      </div>
      <form onSubmit={onFormSubmit}>
        <div className="flex items-center justify-end space-x-6 rounded-b border-t border-gray-200 pt-3 px-3 pb-0">
          <button
            type="button"
            className="rounded-lg bg-red-500 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-reed-800 focus:outline-none focus:ring-4 focus:ring-red-300"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!is_admin}
            title={!is_admin ? "Only admin can edit a user" : null}
            className="rounded-lg bg-blue-700 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:bg-gray-300"
          >
            Save
          </button>
        </div>
        <fieldset className="space-y-6 p-6" disabled={!is_admin}>
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Customer *
              </label>
              <Dropdown
                value={license?.customer?.id || license?.customer || ""}
                onChange={handleInputChange}
                placeholder="Select Customer"
                name="customer"
                options={Customers.filter(
                  (c) => !c.inactive || license?.customer?.id === c.id,
                )}
                optionLabel="name"
                optionValue="id"
                showClear
                valueTemplate={(option, props) => {
                  if (option) {
                    return <div>{option?.name}</div>;
                  }

                  return <span>{props.placeholder}</span>;
                }}
                itemTemplate={(option) => {
                  return <div>{option?.name}</div>;
                }}
                scrollHeight="300px"
                filter
                className="w-full rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 shadow-sm focus:border-blue-600 focus:ring-blue-600"
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                License Key (auto-generated)
              </label>
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 shadow-sm focus:border-blue-600 focus:ring-blue-600"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                required={true}
                name="key"
                value={license.key}
                disabled
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                License Type *
              </label>
              <select
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 shadow-sm focus:border-blue-600 focus:ring-blue-600"
                required={true}
                name="license_type"
                value={license.license_type}
                onChange={handleInputChange}
              >
                <option value="">Select License Type</option>
                {licenseType.filter((lt) => !lt.inactive || lt.license_type === lt.id,).map((lt) => {
                  return (
                    <option key={lt.id} value={lt.id}>
                      {lt.value}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-span-6 sm:col-span-6">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Memo *
              </label>
              <textarea
                className="block h-64 w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 shadow-sm focus:border-blue-600 focus:ring-blue-600"
                rows={2}
                placeholder="Enter Customer Request"
                required={true}
                name="memo"
                value={license.memo}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-3 flex items-center sm:col-span-1">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500"
                name="is_active"
                disabled={!license.is_active}
                onChange={handleInputChange}
                checked={license.is_active}
              />
              <label className="ml-2 text-sm font-medium text-gray-900">
                Active?
              </label>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
};
